import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDButton from 'src/components/md/MDButton';
import Modal from 'src/components/modal/Modal';
import { Grid, Chip, Tooltip, CircularProgress, Backdrop } from '@mui/material';
import { FamousShipmentDTO } from 'src/api/temp/famous/useFamousShipments';
import useSendEdi856Transaction from 'src/api/temp/famous/useSendEdi856Transaction';
import useSyncFamousShipment from 'src/api/temp/famous/useSyncFamousShipment';
import React, { useState } from 'react';
import Icon from '@mui/material/Icon';

interface Props {
  famousShipment: FamousShipmentDTO;
  onClose: (refetch?: boolean) => void;
}
// Custom JSON viewer to match the pattern used elsewhere in the codebase
interface JsonObject {
  [key: string]: JsonValue;
}

type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonObject
  | Array<JsonValue>;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    // Expand first level by default
    root: level < 1,
  });

  if (data === null) return <span style={{ color: '#999' }}>null</span>;
  if (data === undefined)
    return <span style={{ color: '#999' }}>undefined</span>;

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return (
        <span
          style={{
            color: '#009688',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          &quot;{data}&quot;
        </span>
      );
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>{data ? 'true' : 'false'}</span>
      );
    }
    return <span style={{ wordBreak: 'break-word' }}>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span>[ ]</span>;
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root ? '▼' : '▶'} Array [{data.length}]
        </span>

        {expanded.root && (
          <div style={{ paddingLeft: 20, width: '100%' }}>
            {data.map((item, i) => (
              <div key={i} style={{ width: '100%' }}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0, width: '100%' }}>
      <span
        onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {expanded.root ? '▼' : '▶'} Object{' '}
        {`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}
      </span>

      {expanded.root && (
        <div style={{ paddingLeft: 20, width: '100%' }}>
          {keys.map((key) => (
            <div
              key={key}
              style={{
                wordBreak: 'break-word',
                marginBottom: 2,
                width: '100%',
              }}
            >
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper function to get status display info
const getStatusInfo = (status: string) => {
  switch (status) {
    case 'pending':
      return {
        label: 'Pending',
        color: 'default' as const,
        icon: 'schedule',
        description: 'Waiting to fetch data from Famous',
      };
    case 'fetching':
      return {
        label: 'Fetching',
        color: 'info' as const,
        icon: 'sync',
        description: 'Currently fetching data from Famous',
      };
    case 'success':
      return {
        label: 'Success',
        color: 'success' as const,
        icon: 'check_circle',
        description: 'Data successfully fetched from Famous',
      };
    case 'failed':
      return {
        label: 'Failed',
        color: 'error' as const,
        icon: 'error',
        description: 'Failed to fetch data from Famous',
      };
    default:
      return {
        label: status,
        color: 'default' as const,
        icon: 'help',
        description: 'Unknown status',
      };
  }
};

const FamousShipmentModal: React.FC<Props> = ({ famousShipment, onClose }) => {
  const [showRawData, setShowRawData] = useState(false);

  const sendEdi856Transaction = useSendEdi856Transaction(() => {
    onClose(true);
  });

  const syncShipment = useSyncFamousShipment(famousShipment.id);

  const handleSendEdi = () => {
    sendEdi856Transaction.mutate({
      famousShipmentId: famousShipment.id,
    });
  };

  const handleSyncShipment = () => {
    syncShipment.mutate({});
  };

  const statusInfo = getStatusInfo(famousShipment.shipmentFetchStatus);

  const orderDetails = famousShipment.shipmentDetails;
  const customer = orderDetails?.customer;
  const shipTo = orderDetails?.shipTo;
  const carrier = orderDetails?.carrier;

  // Check for translate issues
  const linesWithProducts =
    orderDetails?.lines?.filter((line) => !!line.product) || [];
  const linesWithTranslateIssues = linesWithProducts.filter(
    (line) =>
      !line.product?.translations || line.product.translations.length === 0,
  );
  const hasTranslateIssues = linesWithTranslateIssues.length > 0;
  const missingTranslateProductIds = linesWithTranslateIssues
    .map((line) => line.product?.productIdx)
    .filter(Boolean);

  const missingMessage = (
    <MDBox
      sx={{
        p: 1,
        backgroundColor: '#fff3cd',
        borderRadius: 1,
        border: '1px solid #ffeaa7',
      }}
    >
      <MDTypography variant="caption" color="warning.main">
        Data not available
      </MDTypography>
    </MDBox>
  );

  return (
    <Modal
      maxWidth="xl"
      open={true}
      title={
        <MDBox display="flex" alignItems="center" gap={2}>
          <MDTypography variant="h5" fontWeight="bold">
            Famous Shipment: {famousShipment.famousShipmentId}
          </MDTypography>
          <Chip
            label={statusInfo.label}
            color={statusInfo.color}
            size="small"
            variant="outlined"
          />
          <Tooltip title="Refresh order data from Famous">
            <span>
              <MDButton
                variant="text"
                color="dark"
                onClick={handleSyncShipment}
                disabled={
                  syncShipment.isLoading || !!famousShipment.edi856SentAt
                }
                sx={{
                  minWidth: 'auto',
                  p: 0.5,
                }}
              >
                <Icon sx={{ fontSize: 24, color: 'text.primary' }}>
                  {syncShipment.isLoading ? 'hourglass_empty' : 'refresh'}
                </Icon>
              </MDButton>
            </span>
          </Tooltip>
        </MDBox>
      }
      closeable
      onCloseButton={{
        text: 'Close',
        onClick: () => onClose(),
        disabled: sendEdi856Transaction.isLoading || syncShipment.isLoading,
      }}
    >
      <Grid container spacing={3} sx={{ height: '100%', position: 'relative' }}>
        {/* Loading overlay */}
        <Backdrop
          open={syncShipment.isLoading}
          sx={{
            position: 'absolute',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 2,
          }}
        >
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <CircularProgress size={48} />
            <MDTypography variant="body2" fontWeight="medium">
              Syncing shipment data from Famous...
            </MDTypography>
          </MDBox>
        </Backdrop>

        {/* Error Details - Full Width at Top */}
        {famousShipment.shipmentFetchError && (
          <Grid item xs={12}>
            <MDBox
              sx={{
                p: 2,
                backgroundColor: '#ffebee',
                borderRadius: 1,
                border: '1px solid #ffcdd2',
              }}
            >
              <MDTypography
                variant="caption"
                color="error.main"
                fontWeight="bold"
                sx={{ display: 'block', mb: 1 }}
              >
                ⚠️ Error Details:
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '10px', wordBreak: 'break-word' }}
              >
                {JSON.stringify(famousShipment.shipmentFetchError, null, 2)}
              </MDTypography>
            </MDBox>
          </Grid>
        )}

        <Grid
          item
          xs={8}
          sx={{
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
            p={2}
            sx={{
              borderBottom: '1px solid #e0e0e0',
              flexShrink: 0,
            }}
          >
            <MDTypography variant="h6" fontWeight="bold">
              Shipment Details
            </MDTypography>
          </MDBox>

          <MDBox
            sx={{
              width: '100%',
              overflow: 'auto',
              p: 2,
            }}
          >
            {/* Shipment Summary Section */}
            {orderDetails && (
              <MDBox
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fafafa',
                  flexShrink: 0,
                }}
              >
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  Shipment Summary
                </MDTypography>

                <Grid container spacing={2}>
                  {/* Shipment Information */}
                  <Grid item xs={6}>
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        Shipment Information
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {orderDetails.orderNumber && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Order #:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.orderNumber}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.purchaseOrderNumber && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              PO #:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.purchaseOrderNumber}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.orderDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Order Date:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.orderDate}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.shipDateTime && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Ship Date:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.shipDateTime}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.invoiceDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Invoice Date:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.invoiceDate}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.etaDate && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Delivery Date:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.etaDate}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Carrier Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        Carrier Information
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {carrier?.name && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Carrier:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {carrier.name}
                            </MDTypography>
                          </MDBox>
                        )}
                        {carrier?.SCAC && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              SCAC:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {carrier.SCAC}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.truckLicense && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Truck License:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.truckLicense}
                            </MDTypography>
                          </MDBox>
                        )}
                        {orderDetails.carrierName && (
                          <MDBox mb={0.5}>
                            <MDTypography
                              component="span"
                              variant="caption"
                              fontWeight="bold"
                            >
                              Carrier Name:
                            </MDTypography>
                            <MDTypography
                              component="span"
                              variant="caption"
                              ml={0.5}
                            >
                              {orderDetails.carrierName}
                            </MDTypography>
                          </MDBox>
                        )}
                      </MDBox>
                    </MDBox>
                  </Grid>

                  {/* Customer and Shipping Information */}
                  <Grid item xs={6}>
                    {/* Customer Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        Customer Information
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {customer ? (
                          <>
                            {customer.name && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Name:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.name}
                                </MDTypography>
                              </MDBox>
                            )}
                            {customer.street && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Address:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.street}
                                </MDTypography>
                              </MDBox>
                            )}
                            {customer.city && customer.state && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Location:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {customer.city}, {customer.state}{' '}
                                  {customer.zip}
                                </MDTypography>
                              </MDBox>
                            )}
                          </>
                        ) : (
                          missingMessage
                        )}
                      </MDBox>
                    </MDBox>

                    {/* Ship To Information */}
                    <MDBox mb={2}>
                      <MDTypography
                        variant="subtitle2"
                        fontWeight="bold"
                        color="primary"
                        mb={1}
                      >
                        Ship To Information
                      </MDTypography>
                      <MDBox sx={{ fontSize: '12px', lineHeight: 1.5 }}>
                        {shipTo ? (
                          <>
                            {shipTo.name && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Name:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.name}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.street && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Address:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.street}
                                </MDTypography>
                              </MDBox>
                            )}
                            {shipTo.city && shipTo.state && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  fontWeight="bold"
                                >
                                  Location:
                                </MDTypography>
                                <MDTypography
                                  component="span"
                                  variant="caption"
                                  ml={0.5}
                                >
                                  {shipTo.city}, {shipTo.state}, {shipTo.zip}
                                </MDTypography>
                              </MDBox>
                            )}
                          </>
                        ) : (
                          missingMessage
                        )}
                      </MDBox>
                    </MDBox>
                  </Grid>
                </Grid>

                {/* Line Items Summary */}
                <MDBox mt={3}>
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="primary"
                    mb={2}
                  >
                    Shipment Items ({linesWithProducts.length})
                  </MDTypography>

                  <MDBox
                    sx={{
                      maxHeight: '300px',
                      overflow: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    {linesWithProducts.length > 0 ? (
                      <MDBox sx={{ display: 'flex', flexDirection: 'column' }}>
                        {linesWithProducts.map((line, index) => (
                          <MDBox
                            key={index}
                            sx={{
                              p: 2,
                              borderBottom:
                                index < linesWithProducts.length - 1
                                  ? '1px solid #f0f0f0'
                                  : 'none',
                              '&:hover': {
                                backgroundColor: '#f9f9f9',
                              },
                            }}
                          >
                            <MDBox
                              display="flex"
                              justifyContent="space-between"
                              alignItems="flex-start"
                            >
                              <MDBox flex={1}>
                                <MDBox
                                  display="flex"
                                  alignItems="center"
                                  gap={1}
                                  mb={1}
                                >
                                  <MDTypography
                                    variant="caption"
                                    fontWeight="bold"
                                    sx={{ fontSize: '11px' }}
                                  >
                                    {line.name || 'Product'}
                                  </MDTypography>
                                  {line.product &&
                                  (!line.product.translations ||
                                    line.product.translations.length === 0) ? (
                                    <MDTypography
                                      variant="caption"
                                      sx={{
                                        backgroundColor: '#fff3cd',
                                        color: '#856404',
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontSize: '10px',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      ⚠️ No Translate Data
                                    </MDTypography>
                                  ) : null}
                                </MDBox>

                                {line.product && (
                                  <MDBox sx={{ fontSize: '10px', mb: 1 }}>
                                    <MDTypography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      Product SKU:{' '}
                                      {line.product.translations?.[0]
                                        ?.externalSku ?? 'N/A'}
                                    </MDTypography>
                                    {line.product.commodity && (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                      >
                                        Commodity: {line.product.commodity}
                                      </MDTypography>
                                    )}
                                    {line.product.variety && (
                                      <MDTypography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block' }}
                                      >
                                        Variety: {line.product.variety}
                                      </MDTypography>
                                    )}
                                  </MDBox>
                                )}
                              </MDBox>

                              <MDBox
                                textAlign="right"
                                sx={{ minWidth: '120px' }}
                              >
                                <MDTypography
                                  variant="caption"
                                  sx={{ fontSize: '11px' }}
                                >
                                  {line.quantity?.toLocaleString()}{' '}
                                  {line.unitOfMeasure || 'units'}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        ))}
                      </MDBox>
                    ) : (
                      <MDBox p={3} textAlign="center">
                        <MDTypography variant="caption" color="text.secondary">
                          No line items available
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                </MDBox>
              </MDBox>
            )}

            {/* Raw Data Accordion */}
            <MDBox
              sx={{
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <MDBox
                onClick={() => setShowRawData(!showRawData)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  borderRadius: '4px',
                  flexShrink: 0,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                }}
              >
                <Icon sx={{ fontSize: 18 }}>
                  {showRawData ? 'expand_less' : 'expand_more'}
                </Icon>
                <MDTypography variant="caption" color="text.secondary">
                  Show Raw Sales Order Data (Technical)
                </MDTypography>
              </MDBox>

              {showRawData && (
                <MDBox
                  sx={{
                    display: 'block',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    p: 1,
                    backgroundColor: '#fafafa',
                    flex: 1,
                    overflow: 'auto',
                    minHeight: 0,
                    fontFamily: 'monospace',
                    fontSize: '12px',
                    lineHeight: 1.4,
                  }}
                >
                  <JsonViewer
                    data={
                      famousShipment.shipmentDetails as unknown as JsonValue
                    }
                  />
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Grid>

        {/* Right sidebar for EDI status and actions */}
        <Grid
          item
          xs={4}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
              EDI 856 Status
            </MDTypography>

            {famousShipment.edi856SentAt && famousShipment.edi856Status ? (
              // Already sent - show status and link
              <MDBox>
                <MDBox
                  sx={{
                    p: 2,
                    backgroundColor: '#e8f5e8',
                    borderRadius: 1,
                    border: '1px solid #c8e6c9',
                    mb: 2,
                  }}
                >
                  <MDTypography
                    variant="body2"
                    fontWeight="bold"
                    color="success.main"
                    mb={1}
                  >
                    ✓ EDI 856 Sent
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Sent:{' '}
                    {new Date(famousShipment.edi856SentAt).toLocaleDateString()}
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1 }}
                  >
                    Transaction ID: {famousShipment.edi856Status}
                  </MDTypography>
                </MDBox>

                <MDButton
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    window.open(
                      `https://ui.orderful.com/transactions/${famousShipment.edi856Status}`,
                      '_blank',
                    );
                  }}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  View in Orderful
                </MDButton>
              </MDBox>
            ) : (
              // Not sent yet - show send button
              <MDBox>
                <MDButton
                  variant="contained"
                  color="primary"
                  onClick={handleSendEdi}
                  disabled={
                    sendEdi856Transaction.isLoading ||
                    hasTranslateIssues ||
                    syncShipment.isLoading
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {sendEdi856Transaction.isLoading
                    ? 'Sending...'
                    : hasTranslateIssues
                      ? 'Cannot Send - Missing Translate Data'
                      : 'Send EDI 856 Transaction'}
                </MDButton>

                {hasTranslateIssues && (
                  <MDBox
                    sx={{
                      p: 2,
                      backgroundColor: '#fff3cd',
                      borderRadius: 1,
                      border: '1px solid #ffeaa7',
                      mb: 2,
                    }}
                  >
                    <MDTypography
                      variant="caption"
                      color="warning.main"
                      fontWeight="bold"
                    >
                      ⚠️ Cannot Send EDI: Missing translate data
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 1 }}
                    >
                      Product IDs missing translate data:{' '}
                      {missingTranslateProductIds.join(', ')}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            )}

            <MDBox
              sx={{
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 1,
                border: '1px solid #e9ecef',
              }}
            >
              <MDTypography
                variant="caption"
                color="text.secondary"
                fontWeight="bold"
                sx={{ display: 'block', mb: 1 }}
              >
                About EDI 856
              </MDTypography>
              <MDTypography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '11px', lineHeight: 1.4 }}
              >
                EDI 856 transactions notify customers about shipment status,
                carrier information, and tracking details. This helps customers
                track their shipments and plan for receipt.
              </MDTypography>
            </MDBox>
          </MDBox>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default FamousShipmentModal;
